import { useState, useEffect } from "react";
import axios from "axios";
import { API, mediaUrl } from "../../lib/api";
import { toast } from "sonner";
import { ArrowsClockwise, FolderOpen, Trash, ArrowSquareOut, CheckCircle } from "@phosphor-icons/react";

/**
 * Files diagnostic panel.
 *
 * Lists every uploaded file. All uploads live in Emergent object storage and
 * are served by the backend at /api/files/<path> — files survive redeploys and
 * are reachable from any replica, so the old pod-disk / Nginx 404 class of
 * problems no longer applies.
 */
export default function FilesPanel({ token }) {
  const [data, setData] = useState({ files: [], upload_dir: "", count: 0 });
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/uploads`, { headers: { "X-Admin-Token": token } });
      setData(data);
    } catch {
      toast.error("Could not list uploads. Check admin token / backend.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const remove = async (storagePath) => {
    if (!window.confirm(`Delete this file? It will disappear from the site.`)) return;
    try {
      await axios.post(
        `${API}/admin/uploads/${storagePath}/delete`,
        {},
        { headers: { "X-Admin-Token": token } },
      );
      toast.success("Deleted.");
      load();
    } catch {
      toast.error("Delete failed.");
    }
  };

  const fmtBytes = (b) => {
    if (b < 1024) return `${b} B`;
    if (b < 1024 * 1024) return `${(b/1024).toFixed(1)} KB`;
    return `${(b/(1024*1024)).toFixed(2)} MB`;
  };

  const shortName = (f) => f.original_filename || (f.filename || "").split("/").pop();

  return (
    <div data-testid="files-panel">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-overline mb-1">◢ Uploaded files ({data.count || 0})</div>
          <div className="font-mono text-[10px] text-[#555]">
            Storage: <span className="text-[#DFFF00]">{data.upload_dir || "—"}</span>
          </div>
        </div>
        <button onClick={load} className="btn-secondary"><ArrowsClockwise size={14} /> Refresh</button>
      </div>

      <div className="mb-6 flex items-start gap-3 px-5 py-4 border border-[#00FFB2]/40 bg-[#00FFB2]/[0.05]">
        <CheckCircle size={20} color="#00FFB2" weight="fill" />
        <div className="text-[13px] text-[#F5F5F5] leading-relaxed">
          All uploads are stored in <span className="text-[#00FFB2]">Emergent object storage</span> and served
          by the backend at <code className="text-[#DFFF00]">/api/files/&lt;path&gt;</code>. No VM disk or Nginx
          upload rule is needed anymore — files survive redeploys and are served identically in preview and production.
        </div>
      </div>

      {loading && <div className="text-[#8E8E93] text-sm px-4 py-6">Listing files…</div>}

      {!loading && (data.files || []).length === 0 && (
        <div className="ww-card p-10 text-center">
          <FolderOpen size={28} color="#8E8E93" weight="duotone" />
          <div className="mt-3 font-display text-xl">No uploads yet.</div>
          <p className="mt-2 text-[#8E8E93] text-sm">Upload a cover image, video, or policy document from any CMS section — it will show up here.</p>
        </div>
      )}

      {!loading && (data.files || []).length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {data.files.map((f) => (
            <div key={f.filename} className="ww-card p-4" data-testid={`file-card-${(f.filename || '').split('/').pop()}`}>
              <div className="aspect-[16/10] bg-[#050505] border border-[#1A1A1A] overflow-hidden flex items-center justify-center">
                {f.kind === "image" && <img src={mediaUrl(f.url)} alt="" className="w-full h-full object-cover" />}
                {f.kind === "video" && (
                  <video src={mediaUrl(f.url)} controls preload="metadata" playsInline className="w-full h-full object-contain" />
                )}
                {(f.kind === "document" || f.kind === "other") && (
                  <div className="font-mono text-[10px] text-[#555]">{shortName(f).split(".").pop().toUpperCase()}</div>
                )}
              </div>
              <div className="mt-3 flex items-center gap-2 flex-wrap">
                <span className={`font-mono text-[10px] tracking-[0.18em] uppercase ${f.kind === 'video' ? 'text-[#FF4500]' : (f.kind === 'document' ? 'text-[#00E5FF]' : 'text-[#DFFF00]')}`}>{f.kind}</span>
                <span className="font-mono text-[10px] text-[#8E8E93]">· {fmtBytes(f.size_bytes)}</span>
              </div>
              <div className="mt-2 font-mono text-[11px] text-white truncate" title={shortName(f)}>{shortName(f)}</div>
              <div className="mt-1 font-mono text-[10px] text-[#8E8E93] truncate" title={f.url}>{f.url}</div>
              <div className="mt-3 flex items-center gap-3">
                <a href={mediaUrl(f.url)} target="_blank" rel="noreferrer" className="btn-ghost text-[10px] py-2 px-2">
                  <ArrowSquareOut size={12} /> Open
                </a>
                <button onClick={() => { navigator.clipboard?.writeText(f.url); toast.success("URL copied."); }} className="btn-ghost text-[10px] py-2 px-2">
                  Copy URL
                </button>
                <button onClick={() => remove(f.filename)} className="ml-auto text-[#8E8E93] hover:text-[#FF4500]" title="Delete">
                  <Trash size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
